import { useCallback, useEffect, useState } from "react";
import { AssetItem, MediaModel } from "@/database";
import {
  checkYoutubeThumbnailDimensions,
  getThumbnailUrlUtil,
} from "@/utils/mediaUtils";

interface UseThumbnailSelectionParams {
  formik: any;
  currentMediaDetails: MediaModel;
  assets: AssetItem[];
  mediaBaseUrl: string;
  platformType?: string;
  /**
   * Returns whether the thumbnail may still be auto-synced from the media detail.
   * Once the user has manually picked a thumbnail (or the card is saved) this
   * returns false and the init/assets effects below no longer overwrite it.
   */
  canSyncThumbnail?: () => boolean;
}

export const useThumbnailSelection = ({
  formik,
  currentMediaDetails,
  assets,
  mediaBaseUrl,
  platformType,
  canSyncThumbnail,
}: UseThumbnailSelectionParams) => {
  // Portrait media (orientation === 2) can use thumbnails of any dimension
  const isPortraitMedia = currentMediaDetails?.orientation === 2;
  const [thumbnailDimensionError, setThumbnailDimensionError] = useState<
    string | null
  >(null);
  // Initialize thumbnail from media details when form does not yet have a thumbId
  useEffect(() => {
    const tempThumbId =
      currentMediaDetails?.images?.social ||
      currentMediaDetails?.images?.thumb ||
      currentMediaDetails?.images?.portrait;

    if (!tempThumbId) return;

    if (canSyncThumbnail && !canSyncThumbnail()) {
      return;
    }

    if (formik.values.thumbId) {
      return;
    }

    formik.setFieldValue(
      "thumb",
      getThumbnailUrlUtil({
        media: currentMediaDetails,
        id: tempThumbId,
        source: currentMediaDetails?.source,
        baseUrl:
          assets?.length > 0
            ? (assets.find((a) => a.name === "thumb")?.baseurl ??
                assets[0]?.baseurl) ||
              ""
            : mediaBaseUrl,
      })
    );
    formik.setFieldValue("thumbId", tempThumbId);
  }, [
    currentMediaDetails?.images?.portrait,
    currentMediaDetails?.images?.social,
    currentMediaDetails?.images?.thumb,
    currentMediaDetails?.source,
    assets,
    mediaBaseUrl,
  ]);

  // Update thumbnail URL when assets/baseurl change (mirrors previous BasePublishCard logic)
  useEffect(() => {
    if (canSyncThumbnail && !canSyncThumbnail()) {
      return;
    }

    const thumbBaseUrl =
      assets.find((a) => a.name === "thumb")?.baseurl ?? assets?.[0]?.baseurl;
    if (!(assets?.length > 0 && thumbBaseUrl)) {
      return;
    }

    const tempThumbId =
      currentMediaDetails?.images?.social ||
      currentMediaDetails?.images?.thumb ||
      currentMediaDetails?.images?.portrait;

    if (!tempThumbId) return;

    formik.setFieldValue(
      "thumb",
      getThumbnailUrlUtil({
        media: currentMediaDetails,
        id: tempThumbId,
        source: currentMediaDetails?.source,
        baseUrl: thumbBaseUrl || "",
      })
    );
  }, [assets]);

  // Handler to be passed to ThumbnailSelection
  const handleThumbnailChange = useCallback(
    (_newThumbnailUrl: string, id: string) => {
      if (!id) return;

      const newUrl = getThumbnailUrlUtil({
        media: currentMediaDetails,
        id,
        source: currentMediaDetails?.source,
        baseUrl: assets?.[0]?.baseurl || mediaBaseUrl,
      });

      formik.setFieldValue("thumb", newUrl);
      formik.setFieldValue("thumbId", id);

      if (platformType === "youtube" && !isPortraitMedia) {
        // _180.jpg is a pre-resized thumbnail — replace with .jpg to check
        // the original image dimensions
        const originalUrl = newUrl.replace("_180.jpg", ".jpg");
        checkYoutubeThumbnailDimensions(originalUrl).then(
          setThumbnailDimensionError
        );
      } else {
        setThumbnailDimensionError(null);
      }
    },
    [assets, currentMediaDetails, formik, mediaBaseUrl, platformType, isPortraitMedia]
  );

  return {
    handleThumbnailChange,
    thumbnailDimensionError,
    setThumbnailDimensionError,
  };
};
